import React from 'react';
import '../styles/Legal.css';
import SEO from '../components/SEO';

const Privacy = () => {
  return (
    <div className="legal-page">
      <SEO
        title="Refund & Cancellation Policy"
        description="CADENCE Medical Consulting's refund and cancellation policy for medical consulting services, second opinions, and membership plans."
        path="/privacy"
      />
      <div className="container">
        <div className="legal-content">
          <h1 className="heading-hero">Refund & Cancellation Policy</h1>
          <p className="legal-updated">Last Updated: January 2025</p>

          <section className="legal-section">
            <div className="legal-emphasis" style={{ marginBottom: '2rem', background: 'rgba(74, 14, 78, 0.08)', padding: '1.5rem', borderRadius: '8px' }}>
              <p className="body-large" style={{ margin: 0 }}>
                <strong>IMPORTANT:</strong> Our services involve significant professional time, expertise, and intellectual labor from the moment you engage us. Once work has commenced or deliverables have been provided, services are considered rendered and are <strong>non-refundable</strong>. Please read this policy carefully before purchasing.
              </p>
            </div>
          </section>

          <section className="legal-section">
            <h2 className="heading-2">A. For One-Time Consultation Services</h2>
            <p className="legal-subtitle">(Expert Review, Advocacy Launch, Complete Care Advocacy)</p>

            <div className="legal-subsection">
              <h3 className="heading-3">Services Rendered - Non-Refundable</h3>
              <p className="body-medium">
                <strong>Once any of the following has occurred, your service fee is non-refundable:</strong>
              </p>
              <ul className="legal-list">
                <li>Your intake form has been reviewed and analyzed by our team</li>
                <li>Any consultation, call, or meeting has taken place</li>
                <li>Any report, analysis, recommendation, or written deliverable has been provided to you</li>
                <li>Any advocacy action has been taken on your behalf (including but not limited to: communications with providers, record requests, appointment coordination)</li>
              </ul>
              <p className="body-medium" style={{ marginTop: '1rem' }}>
                <em>You acknowledge that the value of our services lies in the professional expertise, research, analysis, and personalized guidance we provide—not merely the physical deliverable. Once this intellectual work product has been delivered, the service is complete.</em>
              </p>
            </div>

            <div className="legal-subsection">
              <h3 className="heading-3">Pre-Service Cancellation (Before Work Begins)</h3>
              <ul className="legal-list">
                <li><strong>Cancellation more than 72 hours before a scheduled consultation AND before any review work has begun:</strong> 100% refund.</li>
                <li><strong>Cancellation within 72 hours of a scheduled consultation:</strong> 50% refund (covers preparation time already invested).</li>
                <li><strong>"No-show" for a scheduled consultation:</strong> No refund. The consultant's time was reserved and cannot be recovered.</li>
              </ul>
            </div>

            <div className="legal-subsection">
              <h3 className="heading-3">Quality Guarantee (Limited)</h3>
              <p className="body-medium">
                We stand behind our work. If you believe we have <strong>failed to deliver the specific deliverables promised</strong> in your service tier (e.g., a promised report was never provided), you must notify us in writing within 7 calendar days of the promised delivery date. We will:
              </p>
              <ul className="legal-list">
                <li>First, attempt to remedy the issue by providing the missing deliverable</li>
                <li>If we cannot provide the promised deliverable, we will issue a partial refund proportional to the undelivered component</li>
              </ul>
              <p className="body-medium" style={{ marginTop: '1rem', fontWeight: '600' }}>
                This guarantee does NOT apply to:
              </p>
              <ul className="legal-list">
                <li>Disagreement with our professional findings, recommendations, or conclusions</li>
                <li>Outcomes that differ from your expectations or hopes</li>
                <li>Your decision to discontinue or not act on our recommendations</li>
                <li>Subsequent medical diagnoses, test results, or provider opinions that differ from our analysis</li>
                <li>Change of mind or buyer's remorse after receiving our services</li>
              </ul>
            </div>

            <div className="legal-subsection">
              <h3 className="heading-3">Cancellation by Us</h3>
              <p className="body-medium">
                We reserve the right to decline or cancel a service for any reason (e.g., conflict of interest, case falls outside our scope of expertise, client non-compliance). If we cancel before any work is performed, you will receive a full refund. If work has already begun, refunds will be prorated based on work completed.
              </p>
            </div>
          </section>

          <section className="legal-section">
            <h2 className="heading-2">B. For Navigator & Advocate Membership Subscriptions</h2>

            <div className="legal-subsection">
              <h3 className="heading-3">Annual Plan Cooling-Off Period</h3>
              <p className="body-medium">
                You may cancel your annual membership within the first 14 days of the contract start date for a full refund, <strong>provided no services have been rendered</strong> (no consultations, communications, or advocacy actions have occurred).
              </p>
            </div>

            <div className="legal-subsection">
              <h3 className="heading-3">Cancellation After Cooling-Off Period or Services Rendered</h3>
              <p className="body-medium">
                After 14 days, OR once any membership services have been utilized (whichever comes first), membership fees are <strong>non-refundable</strong>. You may cancel at any time, and your access will continue until the end of your pre-paid term.
              </p>
            </div>

            <div className="legal-subsection">
              <h3 className="heading-3">Cancellation for Cause</h3>
              <p className="body-medium">
                We reserve the right to terminate a membership for violations of our Terms of Service. Any refund for unused services is at our sole discretion.
              </p>
            </div>

            <div className="legal-subsection">
              <h3 className="heading-3">Non-Renewal</h3>
              <p className="body-medium">
                Memberships do not auto-renew. You will be notified before your term ends with an option to renew.
              </p>
            </div>
          </section>

          <section className="legal-section">
            <h2 className="heading-2">C. Chargebacks & Disputes</h2>
            <p className="body-medium">
              If you initiate a credit card chargeback or payment dispute after services have been rendered, you acknowledge that:
            </p>
            <ul className="legal-list">
              <li>We will provide documentation to your payment provider demonstrating services were delivered</li>
              <li>Fraudulent chargebacks may be referred to collections and/or reported to credit agencies</li>
              <li>You will be responsible for any chargeback fees, collection costs, and legal fees incurred</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="heading-2">Clinical Consulting Agreement</h2>
            <p className="body-medium">
              This Agreement is between CADENCE Medical Consulting LLC ("Clinical Consultant") and the Client.
            </p>

            <div className="legal-subsection">
              <h3 className="heading-3">1. Clinical Services</h3>
              <p className="body-medium">
                Consultant will provide diagnostic, navigational, and advocacy services as outlined in the selected tier. These are consulting services and do not establish a traditional treatment-based provider-patient relationship.
              </p>
            </div>

            <div className="legal-subsection">
              <h3 className="heading-3">2. Terms & Fees</h3>
              <p className="body-medium">
                Membership is month-to-month. Fees are billed monthly in advance. Annual prepayment incurs a 10% discount.
              </p>
            </div>

            <div className="legal-subsection">
              <h3 className="heading-3">3. Acknowledgment of Non-Refundable Services</h3>
              <p className="body-medium">
                By purchasing services, Client acknowledges they have read and understood this Refund Policy and agrees that once services are rendered, fees are non-refundable except as explicitly stated above.
              </p>
            </div>

            <div className="legal-subsection">
              <h3 className="heading-3">4. Limitation of Liability</h3>
              <p className="body-medium">
                Consultant's total liability is limited to the fees paid for services. Client understands Consultant does not provide medical treatment or emergency services.
              </p>
            </div>
          </section>

          <section className="legal-section">
            <h2 className="heading-2">Compliance & Security Documents</h2>
            <p className="body-medium">
              For investors, partners, and covered entities, the following documents describe our
              platform, our third-party subprocessors, and our HIPAA Business Associate Agreement
              framework. These materials are provided as drafts for review and do not constitute legal
              advice or a certification of compliance.
            </p>
            <ul className="legal-list">
              <li>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/compliance/exec-summary`} target="_blank" rel="noopener noreferrer" data-testid="download-exec-summary">
                  Executive Summary (PDF)
                </a>
              </li>
              <li>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/compliance/subprocessors`} target="_blank" rel="noopener noreferrer" data-testid="download-subprocessors">
                  Subprocessor & BAA Overview (PDF)
                </a>
              </li>
              <li>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/compliance/baa-template`} target="_blank" rel="noopener noreferrer" data-testid="download-baa-template">
                  Business Associate Agreement — Template (PDF)
                </a>
              </li>
            </ul>
          </section>

          <section className="legal-section contact-section">
            <h2 className="heading-2">Questions?</h2>
            <p className="body-large">
              If you have any questions about our Refund & Cancellation Policy, please contact us at{' '}
              <a href="mailto:CADENCE@cadencemedicalconsulting.com">CADENCE@cadencemedicalconsulting.com</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;